<template>
  <div>
    <form @submit.prevent="submit">
      <h1>{{ $t("signinPage.title") }}</h1>
      <router-link class="sign-up-link" :to="signUpLocation">{{ $t("signinPage.signUpLink") }}</router-link>
      <text-field
        class="input"
        type="email"
        :label="$t('signinPage.emailLabel')"
        :placeholder="$t('signinPage.emailPlaceholder')"
        v-model="email"
      />
      <text-field
        class="input"
        type="password"
        :label="$t('signinPage.passwordLabel')"
        :placeholder="$t('signinPage.passwordPlaceholder')"
        v-model="password"
      />
      <div class="sign-in-container">
        <p class="sign-in-txt">
          {{ $t("signinPage.agreementText") }}
          <a href="/terms" target="blank">{{ $t("common.termsOfService") }}</a
          >{{ $t("signinPage.and") }}<a href="/privacy" target="blank">{{ $t("signinPage.privacyPolicy") }}</a>
          <span v-html="$t('signinPage.agreementTextSuffix')"></span>
        </p>
        <button-default class="login-button" :disabled="submitting">
          <span v-if="submitting" class="login-button-content">
            <icon-spinner class="login-button-spinner" />
            {{ $t("common.loggingIn") }}
          </span>
          <span v-else>{{ $t("common.login") }}</span>
        </button-default>
      </div>
      <div class="setting">
        <router-link :to="{ name: 'resetPassword' }">{{ $t("signinPage.forgotPasswordLink") }}</router-link>
        <a class="link" :href="help" target="blank">{{ $t("signinPage.helpLink") }}</a>
      </div>
    </form>
    <div v-if="message" v-html="message"></div>
  </div>
</template>
<script lang="ts">
import Vue from "vue";
import { Location } from "vue-router";
import TextField from "@/components/molecules/TextField.vue";
import ButtonDefault from "@/components/atoms/ButtonDefault.vue";
import { writerTypeIndicatorRequestType } from "@/store/modules/writerTypeIndicator";
import { Auth } from "aws-amplify";
import { resolveSafeRedirect, withRedirect } from "@/lib/utils/authRedirect";
import IconSpinner from "~icons/figma/Spinner";

interface Props {
  propEmail: string;
}

interface Data {
  email: string;
  password: string;
  message: string;
  submitting: boolean;
}

interface Computed {
  help: string;
  contestRedirect: string | null;
  signUpLocation: Location;
}

interface Methods {
  submit(): void;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: { ButtonDefault, IconSpinner, TextField },
  props: {
    propEmail: { type: String, default: "" },
  },
  data() {
    return {
      email: this.propEmail,
      password: "",
      message: "",
      submitting: false,
    };
  },
  computed: {
    help() {
      return process.env.VUE_APP_NOTION_HELP!;
    },
    contestRedirect() {
      return resolveSafeRedirect(this.$route.query.redirect);
    },
    signUpLocation() {
      return withRedirect({ name: "signUp" }, this.contestRedirect);
    },
  },
  methods: {
    async submit() {
      if (this.submitting) return;
      this.submitting = true;

      const { email, password } = this;

      try {
        const res = await Auth.signIn(email, password);

        if (res.challengeName === "NEW_PASSWORD_REQUIRED") {
          this.$router.push({ name: "newPassword" });
        } else if (res) {
          // 作家タイプ診断のゲストデータ確認
          const requestType: writerTypeIndicatorRequestType = "signin";
          await this.$store.dispatch("writerTypeIndicatorModule/checkGuestResult", requestType);

          window.location.href = this.contestRedirect || "/home";
          // 画面遷移するためここでは submitting を解除しない（リダイレクト直前のクリックを抑止）
        } else {
          throw new Error("error");
        }
      } catch (err: any) {
        if (err.code === "UserNotConfirmedException") {
          this.$router.push(
            withRedirect(
              {
                name: "resendConfirmCode",
                params: {
                  propEmail: email,
                  propMessage: this.$t("signinPage.confirmCodeNotEntered"),
                },
              },
              this.contestRedirect
            )
          );
        } else {
          this.message = this.$t("signinPage.invalidCredentials");
        }
        setTimeout(() => {
          this.message = "";
        }, 3000);
        this.submitting = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
h1 {
  font-size: var(--font-size-md);
  text-align: center;
  color: var(--color-text-normal);
}

.sign-up-link {
  color: var(--color-primary);
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 15px;
}

.input {
  margin-bottom: 15px;
  --text-field-bg: var(--color-surface-base);
  --text-field-border: none;
  --text-field-icon-color: var(--color-text-low);
}

.setting {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 0.67em;

  a {
    color: var(--color-primary);
  }
}

.sign-in-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 15px;

  .sign-in-txt {
    color: var(--color-text-normal);
    margin: 0 0 15px;
    font-size: var(--font-size-xs);

    @include mq(sp) {
      font-size: var(--font-size-xs);
    }

    a {
      text-decoration: none;
      color: var(--color-primary);
    }
  }

  .login-button {
    margin: 0;
    width: 100%;
    border-radius: var(--radius-rounded);
    background-color: var(--color-primary);
    border-color: var(--color-primary);
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-on-fixed-bg);
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }

  .login-button-content {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .login-button-spinner {
    font-size: var(--font-size-md);
    color: currentColor;
    animation: login-spinner-rotate 0.8s linear infinite;
  }
}

@keyframes login-spinner-rotate {
  to {
    transform: rotate(360deg);
  }
}
</style>
